import React, { Component } from 'react';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import BlazeTable from 'components/blazeTable/BlazeTable';
import i18n from 'i18n';
import _ from 'lodash';
import styles from 'views/Analytics/AnalyticsMockService/Inspection/RequestDetail/EventList/AnalyticsEventList.module.css';
import HelpTooltip from 'components/overlays/helpTooltip/HelpTooltip';
import { WithTranslation, withTranslation } from 'react-i18next';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Button, OverlayTrigger, Popover } from 'react-bootstrap';
import BzmIcon from 'components/icons/BzmIcon';
import Truncate from 'react-truncate';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { connect } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import { InspectionViewEvent } from 'types/rest/analyticsTypes';
import { formatDateTimeFromSeconds } from 'utils/dateUtil';
import EventTypesLegend from 'views/Analytics/AnalyticsMockService/Inspection/RequestDetail/EventList/EventTypesLegend';
import clsx from 'clsx';
import { DEFAULT_POPPER_CONFIG } from '../../../../../../constants/ui-constants';

interface RequestsProps {
  eventGroupName: string;
  executionSteps: InspectionViewEvent[];
}

interface EventListStateProps {
  eventGroupName: string;
  events: InspectionViewEvent[];
  filter: boolean;
}

const mapDispatchToProps = {
  showToastNotification,
};

/**
 * List of events. Used in Matching and Request Detail tab.
 */
class AnalyticsEventList extends Component<AllProps, EventListStateProps> {
  tableRef: any = React.createRef<BlazeTable>();

  state = {
    eventGroupName: 'EVENTS',
    events: this.props.executionSteps,
    filter: false,
  };

  // Definition of table columns
  colsDefinition: BlazeTableColDef[] = [
    {
      header: i18n.t('analytics:events.table.event'),
      width: 50,
      accessor: 'name',
    },
    {
      header: i18n.t('analytics:events.table.timestamp'),
      width: 50,
      accessor: 'timestamp',
      cellComponent: (rowData: InspectionViewEvent) => {
        if (_.isNil(rowData.timestamp)) {
          return null;
        }
        return formatDateTimeFromSeconds(rowData.timestamp / 1000);
      },
    },
    {
      header: i18n.t('analytics:events.table.shortInfo'),
      width: 50,
      accessor: 'shortInfo',
    },
    {
      header: i18n.t('analytics:events.table.longInfo'),
      width: 110,
      cellClass: 'flex-table-col-overflow-visible',
      cellComponent: (rowData: InspectionViewEvent) => {
        if (_.isNil(rowData.longInfo)) {
          return null;
        }
        return this.truncate(rowData.longInfo);
      },
      accessor: 'longInfo',
    },
  ];

  copyDataToClipboard = (endpointText: string) => {
    try {
      const el = document.createElement('textarea');
      el.value = endpointText;
      el.setAttribute('readonly', '');
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.props.showToastNotification(ToastType.Success, i18n.t('analytics:successMsgs.copyDataSuccessMsg'));
    } catch (e) {
      this.props.showToastNotification(
        ToastType.Danger,
        i18n.t('analytics:errorMsgs.errorTitle'),
        i18n.t('analytics:errorMsgs.failedToCopyToClipboard'),
      );
    }
  };

  truncate = (longInfo: string) => {
    const ellipsisWithOverlay = (
      <>
        <OverlayTrigger
          trigger="click"
          key="22"
          placement="left"
          popperConfig={DEFAULT_POPPER_CONFIG}
          rootClose
          overlay={
            <Popover
              style={{ maxWidth: '400px' }}
              id={'popoverId'}
              className={`popover-positioned-right`}
              title={i18n.t('analytics:eventList.fullContentPreview')}
            >
              {longInfo.length > 400 ? longInfo.substring(0, 400) + '...' : longInfo}

              <hr />

              <div className={'text-right'}>
                <Button
                  className="btn-link btn-no-hover"
                  variant={'secondary'}
                  title={i18n.t('analytics:eventList.copyContentTitle')}
                  onClick={() => this.copyDataToClipboard(longInfo)}
                >
                  <BzmIcon className="icon-xs" variant={'icon-bzm-clipboard'}></BzmIcon>
                  <span className="mr-2" style={{ position: 'relative', top: '-2px' }}>
                    {' '}
                    {i18n.t('analytics:eventList.copyContent')}
                  </span>
                </Button>
              </div>
            </Popover>
          }
        >
          {/* Actual button with overlay */}
          <button type="button" className="p-0 btn btn-link-dark btn-no-hover ml-2" title="Download">
            <BzmIcon variant={'icon-bzm-ellipsis-h-circle'} className={'icon-xs'} />
          </button>
          {/* Actual button with overlay */}
        </OverlayTrigger>
      </>
    );

    return (
      // Inline width 95% is set because we have autoscroll enabled and scrollbar occupies some space
      <div style={{ width: '95%' }}>
        <Truncate lines={1} ellipsis={ellipsisWithOverlay}>
          {longInfo}
        </Truncate>
      </div>
    );
  };

  tableOptions: BlazeTableOptions = {
    showRowExpander: false,
    columnDef: this.colsDefinition,
    pageable: true,
    selectable: false,
    pageSize: 5,
    minWidth: 950,
    minHeight: 150,
    tableName: 'eventList',
    noResultsText: i18n.t('analytics:inspection.noResultsText'),
    customRowClassName: (rowData: InspectionViewEvent) => {
      let customRow = '';
      switch (rowData.category) {
        case 'SET_PROPERTIES':
          customRow = styles.eventSetProp;
          break;
        case 'ASSERT':
          customRow = styles.eventAssert;
          break;
        case 'ERROR':
          customRow = styles.eventError;
          break;
        case 'STEP_FAILED':
          customRow = styles.eventStepFailed;
          break;
        case 'LOG_WARNING':
          customRow = styles.eventLogWarn;
          break;
        case 'START':
          customRow = styles.eventStart;
          break;
      }
      return clsx(customRow, styles.eventRow);
    },
    customHeaderClassName: 'flex-table-item-top',
    customTableClassName: 'flex-table-simple flex-table-condensed',
    maxContentHeight: '240px',
  };

  render() {
    const { t } = this.props;
    return (
      <>
        <React.Fragment>
          <Row className="align-items-end">
            <Col>
              <h2 className="mb-4">
                {this.props.eventGroupName} ({this.state.events ? this.state.events.length : 0})
                <HelpTooltip
                  id={'ms-request-matches-help'}
                  fontSize={22}
                  title={t('common:showHelp')}
                  className="ml-3"
                  style={{ position: 'relative', top: '2px' }}
                />
              </h2>
            </Col>
            <Col>
              <div className="float-right d-inline-block">
                <EventTypesLegend executionSteps={this.state.events} />
              </div>
            </Col>
          </Row>
          <div>
            <BlazeTable
              options={this.tableOptions}
              data={this.props.executionSteps}
              onRef={(ref: any) => (this.tableRef = ref)}
              showPageSizeDropdown={true}
            />
          </div>
        </React.Fragment>
      </>
    );
  }
}

const mapStateToProps = ({ analytics }: ApplicationState) => {
  return {
    isLoading: analytics.ui.isLoading,
  };
};

type MapStateToProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type AllProps = RequestsProps & DispatchProps & MapStateToProps & WithTranslation;

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(AnalyticsEventList));
